import { ReactNode } from 'react';

import { GroupBase, MenuListProps, components, wrapMenuList } from '@any-ui-react/select';

export interface SelectAllMenuListState {
  label: ReactNode;
  disabled: boolean;
  onSelectAll: () => void;
}

/**
 * Adds a "select all" action to the bottom of the account menu.
 *
 * It lives inside MenuList rather than Menu because MenuList is the scroll
 * container — `sticky bottom-0` pins the action while the paginated option list
 * scrolls behind it — and because LazySelectV2 owns the Menu slot.
 *
 * `wrapMenuList` is required: AsyncPaginateSelect hangs its "load the next page on
 * scroll" logic off MenuList, so replacing the slot without wrapping would leave the
 * menu stuck on the first page of accounts.
 *
 * State arrives through a ref rather than as arguments so the returned component
 * keeps one identity for the life of the select. Rebuilding it per render would
 * change the element type React sees and remount the menu — losing both scroll
 * position and the pagination state that lives with it — every time the parent
 * re-renders with a fresh onChange closure.
 */
export const createSelectAllMenuList = <
  Option,
  IsMulti extends boolean,
  Group extends GroupBase<Option>,
>(
  stateRef: { current: SelectAllMenuListState }
) =>
  wrapMenuList<Option, IsMulti, Group>((props: MenuListProps<Option, IsMulti, Group>) => {
    const { label, disabled, onSelectAll } = stateRef.current;

    return (
      <components.MenuList {...props}>
        {props.children}
        <div className="bg-gray-0 border-gray-2 sticky bottom-0 border-t">
          <button
            type="button"
            className="text-link w-full cursor-pointer px-3 py-2 text-left text-sm disabled:cursor-not-allowed disabled:opacity-50"
            disabled={disabled}
            // react-select closes the menu when the input blurs, and a mousedown inside
            // the menu blurs it before onClick has a chance to run.
            onMouseDown={(event) => event.preventDefault()}
            onClick={onSelectAll}
          >
            {label}
          </button>
        </div>
      </components.MenuList>
    );
  });
